import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { generateAnonymousId, getAnonymousId } from '../services/analytics';
import { customerApi } from '../services/api';
import {
  authApi,
  type LoginStartRequest,
  type LoginStartResponse,
  type LoginVerifyRequest,
  type LoginVerifyResponse,
} from '../services/auth';

// Define Address type locally or import from where it's defined (e.g., services/api.ts?)
interface Address {
  id: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

// UserInfo type is defined locally
interface UserInfo {
  id: string;
  email: string | null;
  phone: string | null;
  username: string | null;
  is_active: boolean;
  last_login?: string | null;
}

interface AuthState {
  setAuthData: (authData: { accessToken: string; customerId: string; refreshToken: string }) => void;
  // Authentication state
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  email: string | null;
  userId: string | null;
  username: string | null; // Add username to store
  isEmailVerified: boolean | null;

  // User Profile Info
  firstName: string | null;
  lastName: string | null;
  address: Address | null; // Uses locally defined Address type
  phone: string | null;
  dateJoined: string | null; // Will get from registrationInfo
  lastLogin: string | null;
  isLoadingProfile: boolean;
  profileError: string | null;
  registrationStatus: string | null;
  state: string | null;
  customerId: string | null;

  // OTP verification state
  otpVerificationInProgress: boolean;
  // Temp storage for user ID between login steps
  pendingUserId: string | null;

  // Anonymous user tracking
  anonymousId: string | null;
  isAnonymousIdRegistered: boolean;
  anonymousUserData: {
    first_seen_at?: string;
    referrer?: string;
    initial_utm_source?: string;
    initial_utm_medium?: string;
    initial_utm_campaign?: string;
    initial_landing_page?: string;
  } | null;

  // Password Change state & action
  isChangingPassword: boolean;
  changePasswordError: string | null;
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<{ success: boolean; message?: { msg?: string }[] }>;

  // Auth actions (UPDATED)
  loginStart: (loginData: LoginStartRequest) => Promise<LoginStartResponse>;
  loginVerify: (verificationData: { code: string; otp_channel: 'email' | 'sms' }) => Promise<LoginVerifyResponse>;
  loadUserProfile: () => Promise<void>;
  cancelOTPLogin: () => void;
  logout: () => void;
  refreshAccessToken: () => Promise<string | null>;

  // Anonymous user actions
  initializeAnonymousUser: () => Promise<string>;
  getAnonymousId: () => string | null;
  clearAnonymousUser: () => void;
}

// Define the persist options separately for clarity
// type AuthPersist = (config: StateCreator<AuthState>, options: PersistOptions<AuthState>) => StateCreator<AuthState>;

// Define initial state as a constant for easy reset
const initialAuthState = {
  // Authentication state
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  email: null,
  userId: null,
  username: null,
  isEmailVerified: false,

  // User Profile state
  firstName: null,
  lastName: null,
  address: null,
  phone: null,
  dateJoined: null,
  lastLogin: null,
  isLoadingProfile: false,
  profileError: null,
  registrationStatus: null,
  state: null,
  customerId: null,

  // OTP state
  otpVerificationInProgress: false,
  pendingUserId: null,

  // Anonymous user state
  anonymousId: null,
  isAnonymousIdRegistered: false,
  anonymousUserData: null,

  // Password Change state
  isChangingPassword: false,
  changePasswordError: null,
};

export const useAuthStore = create<AuthState>(
  // Cast persist to any to bypass complex type issues for now
  (persist as any)(
    (set: any, get: any) => ({
      // Add any types back to set/get if needed
      // Initial state
      ...initialAuthState,

      // Step 1: Start Login Process
      loginStart: async (loginData: LoginStartRequest): Promise<LoginStartResponse> => {
        set({ otpVerificationInProgress: false, pendingUserId: null }); // Reset state before attempt
        try {
          // Call the new loginStart service function from authApi
          const response = await authApi.loginStart(loginData);

          if (response.success && response.user_id) {
            // Store the pending user ID and set OTP flag
            set({
              email: loginData.email, // Store email from input
              pendingUserId: response.user_id,
              otpVerificationInProgress: true,
              isAuthenticated: false, // Not authenticated yet
              token: null, // Ensure tokens are null at this stage
              refreshToken: null,
            });
          } else {
            // Clear pending state if login start failed (e.g., invalid credentials)
            set({ pendingUserId: null, otpVerificationInProgress: false });
          }

          return response; // Return the raw response (contains success/message)
        } catch (error) {
          console.error('Login start failed:', error);
          // Clear pending state on any error during the start phase
          set({ pendingUserId: null, otpVerificationInProgress: false });
          throw error; // Re-throw error for the component to handle
        }
      },

      setAuthData: async (authData: { accessToken: string; customerId: string; refreshToken: string }) => {
        set({
          token: authData.accessToken,
          refreshToken: authData.refreshToken,
          isAuthenticated: true,
          customerId: authData.customerId,
          otpVerificationInProgress: false,
          pendingUserId: null,
        });
        try {
          await get().loadUserProfile();
        } catch (loadError) {
          console.error('Failed to load user profile or applicant status post-login:', loadError);
          // Non-fatal, login succeeded but profile/status load failed
        }

        // Create alias between anonymous and authenticated user
        const anonymousId = get().anonymousId;
        if (anonymousId) {
          try {
            await authApi.createAlias(authData.accessToken);
          } catch (error) {
            console.error('Failed to create analytics alias:', error);
          }
        }
      },

      // Step 2: Verify OTP and Complete Login
      loginVerify: async (verificationData: {
        code: string;
        otp_channel: 'email' | 'sms';
        customer_profile_id: string;
      }): Promise<LoginVerifyResponse> => {
        const { pendingUserId } = get();
        if (!pendingUserId) {
          console.error('Login verify called without pendingUserId');
          throw new Error(
            'The verification code you entered is incorrect. Please request a new code and enter the most recent one you receive.',
          );
        }

        try {
          // Prepare data for the loginVerify service call
          const verifyRequestData: LoginVerifyRequest = {
            user_id: pendingUserId,
            code: verificationData.code,
            otp_channel: verificationData.otp_channel,
          };

          // Verify OTP code via the service
          const response = await authApi.loginVerify(verifyRequestData);

          // Store auth data (Tokens, final user ID, set authenticated flag)
          set({
            token: response.access,
            refreshToken: response.refresh,
            isAuthenticated: true,
            userId: response.user_id, // Store the final authenticated user ID
            otpVerificationInProgress: false,
            customerId: response.customer_profile_id,
            pendingUserId: null, // Clear pending user ID after successful verification
            // email is already set from loginStart step
          });

          // Load user profile AND applicant status after successful login
          try {
            await get().loadUserProfile();
          } catch (loadError) {
            console.error('Failed to load user profile or applicant status post-login:', loadError);
            // Non-fatal, login succeeded but profile/status load failed
          }

          // Create alias between anonymous and authenticated user
          const anonymousId = get().anonymousId;
          if (anonymousId) {
            try {
              await authApi.createAlias(response.access);
            } catch (error) {
              console.error('Failed to create analytics alias:', error);
            }
          }

          return response; // Return the raw response (contains tokens)
        } catch (error) {
          console.error('OTP verification failed:', error);
          // Clear pending state on verification error
          set({ pendingUserId: null, otpVerificationInProgress: false });
          throw error; // Re-throw error for the component to handle
        }
      },

      // Load user profile from the API (Expanded)
      loadUserProfile: async () => {
        const token = get().token;

        if (!token || !get().isAuthenticated) {
          set({ isLoadingProfile: false, profileError: 'Authentication required.' });
          return; // Exit if not authenticated
        }

        set({ isLoadingProfile: true, profileError: null });

        try {
          // UserInfo type now matches the one used in authApi.getCurrentUser
          const userPromise: Promise<UserInfo> = authApi.getCurrentUser(token);
          const customerProfilePromise = customerApi.getProfile(get().customerId);

          const [userInfo, customerProfile] = await Promise.all([userPromise, customerProfilePromise]);

          // Combine data and update store
          set({
            email: userInfo?.email ?? customerProfile?.email ?? null,
            username: userInfo?.username ?? null, // Store username
            phone: userInfo?.phone ?? customerProfile?.phone ?? null, // Use phone from UserInfo first now
            firstName: customerProfile?.firstName ?? null, // Get names primarily from registration
            lastName: customerProfile?.lastName ?? null,
            dateJoined: customerProfile?.createdAt ?? null, // Get dateJoined from registration created_at
            lastLogin: userInfo?.last_login ?? null,
            isLoadingProfile: false,
            profileError: null,
            registrationStatus: customerProfile?.registrationStatus ?? null,
            state: customerProfile?.state ?? null,
            userId: userInfo?.id ?? get().userId,
          });
        } catch (error: any) {
          console.error('Failed to load full user profile:', error);
          const errorMsg = 'Failed to load profile information.';
          if (error.response?.status === 404 && error.config?.url?.includes('/registrations/me')) {
            console.warn('User registration not found, profile may be incomplete.');
            try {
              const userInfoPartial: UserInfo = await authApi.getCurrentUser(token);
              set({
                email: userInfoPartial?.email ?? null,
                username: userInfoPartial?.username ?? null, // Set username
                phone: userInfoPartial?.phone ?? null, // Set phone
                // dateJoined cannot be reliably set without registration
                dateJoined: null,
                lastLogin: userInfoPartial?.last_login ?? null,
                isLoadingProfile: false,
                profileError: 'Registration data not found.', // Non-blocking error
              });
            } catch (userFetchError) {
              set({ isLoadingProfile: false, profileError: errorMsg });
            }
          } else {
            set({ isLoadingProfile: false, profileError: errorMsg });
          }
        }
      },

      // Cancel OTP login process
      cancelOTPLogin: () => {
        set({
          otpVerificationInProgress: false,
          pendingUserId: null, // Clear the pending user ID if user backs out
        });
      },

      // Logout function - Only handle auth-specific cleanup
      logout: () => {
        const currentAnonymousId = get().anonymousId;
        const currentIsAnonymousIdRegistered = get().isAnonymousIdRegistered;
        const currentAnonymousUserData = get().anonymousUserData;

        // Reset to initial state but preserve anonymous user tracking
        set({
          ...initialAuthState,
          anonymousId: currentAnonymousId,
          isAnonymousIdRegistered: currentIsAnonymousIdRegistered,
          anonymousUserData: currentAnonymousUserData,
        });
      },

      // Token refresh function
      refreshAccessToken: async () => {
        try {
          const newToken = await authApi.refreshToken();
          set({ token: newToken });
          if (newToken) {
            await get().loadUserProfile();
          }
          return newToken;
        } catch (error) {
          console.error('Token refresh failed:', error);
          get().logout();
          return null;
        }
      },

      // Change Password action (for authenticated users)
      changePassword: async (currentPassword: string, newPassword: string) => {
        const token = get().token;
        if (!token) {
          set({ changePasswordError: 'Authentication required.', isChangingPassword: false });
          throw new Error('Authentication required to change password.');
        }

        set({ isChangingPassword: true, changePasswordError: null });

        try {
          // Assume authApi.changePassword exists (will add in next step)
          const response = await authApi.changePassword(token, {
            current_password: currentPassword,
            new_password: newPassword,
          });

          // Assuming the API returns { message: "..." } on success
          set({ isChangingPassword: false });
          return { success: true, message: response.message }; // Return success status
        } catch (error: any) {
          console.error('Password change failed:', error);
          const errorMsg = error.response?.data?.detail || error.message || 'Failed to change password.';
          set({ changePasswordError: errorMsg, isChangingPassword: false });
          // Re-throw the error so the component can catch it if needed
          // Or return a structured error response
          // throw error;
          return { success: false, message: errorMsg };
        }
      },

      // Initialize anonymous user ID if not present
      initializeAnonymousUser: async () => {
        try {
          // Get or generate anonymous ID
          let anonymousId = getAnonymousId();
          if (!anonymousId) {
            anonymousId = generateAnonymousId();
            try {
              localStorage.setItem('anonymousId', anonymousId);
              try {
                document.cookie = `anonymous_id=${anonymousId}; path=/; max-age=31536000; SameSite=Lax`;
              } catch (cookieError) {
                console.warn('Failed to set anonymous ID cookie:', cookieError);
              }
            } catch (storageError) {
              console.warn('Failed to set anonymous ID in localStorage:', storageError);
              // Continue with the anonymousId in memory only
            }
          }

          set({
            anonymousId,
            isAnonymousIdRegistered: true,
          });

          return anonymousId;
        } catch (error) {
          console.error('Failed to initialize anonymous user:', error);
          // Return a temporary ID but don't update state
          return generateAnonymousId();
        }
      },

      // Get the current anonymous ID
      getAnonymousId: () => {
        const storeAnonymousId = get().anonymousId;
        if (storeAnonymousId) {
          return storeAnonymousId;
        }

        try {
          return getAnonymousId();
        } catch (error) {
          console.warn('Error getting anonymous ID:', error);
          return null;
        }
      },

      // Clear anonymous user data
      clearAnonymousUser: () => {
        set({
          anonymousId: null,
          isAnonymousIdRegistered: false,
          anonymousUserData: null,
        });
        try {
          localStorage.removeItem('anonymousId');
          document.cookie = 'anonymous_id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        } catch (error) {
          console.warn('Error clearing anonymous user data:', error);
        }
      },
      partialize: (state: AuthState) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        email: state.email,
        userId: state.userId,
        username: state.username, // Persist username
        anonymousId: state.anonymousId,
        isAnonymousIdRegistered: state.isAnonymousIdRegistered,
        anonymousUserData: state.anonymousUserData,
      }),
    }),
    {
      name: 'auth-storage',
      // Define which parts of the state to persist
      partialize: (state: AuthState) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        email: state.email,
        userId: state.userId,
        username: state.username,
        anonymousId: state.anonymousId,
        isAnonymousIdRegistered: state.isAnonymousIdRegistered,
        anonymousUserData: state.anonymousUserData,
        registrationStatus: state.registrationStatus,
        customerId: state.customerId,
        state: state.state,
      }),
      // Custom storage
      storage: {
        getItem: (key: string) => {
          const value = localStorage.getItem(key);
          return value ? JSON.parse(value) : null;
        },
        setItem: (key: string, value: any) => {
          localStorage.setItem(key, JSON.stringify(value));
        },
        removeItem: (key: string) => localStorage.removeItem(key),
      },
    },
  ),
);
